import { NovaModalDialog } from '#components'

const useApplyCreatorProcess = () => {
  const { t } = useNuxtApp().$i18n
  const { show: modalShow, hide: modalHide } = useModal()
  const { showSignInDialog } = useMembershipProcess()
  const { certifiedKYC } = useKyc()
  const userStore = useUserStore()
  const mypageStore = useMyPageStore()
  const { gtEvent } = useGoogleTag()

  const dialog = ({
    title,
    content,
    confirmLabel,
    confirm,
  }: {
    title: string
    content: string
    confirmLabel?: string
    confirm?: () => void
  }) => {
    modalShow({
      component: NovaModalDialog,
      bind: {
        name: modalsName.MODAL_DIALOG,
        contentsTextAlign: 'center',
        btns: [
          {
            label: confirmLabel || t('confirm'),
            theme: 'primary-blue-dark',
            size: 32,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
              confirm?.()
            },
          },
        ],
      },
      slots: {
        title,
        content,
      },
    })
  }

  const callDialog = {
    // 1. 전환 신청 > 이미 크리에이터 전환 완료
    alreadyCreator: () =>
      dialog({
        title: t('applyCreator.guide.apply.notificationTitle'),
        content: t('applyCreator.guide.apply.message.failAlreadyCreator'),
      }),
    // 2. 전환 신청 > 반려
    reject: () =>
      dialog({
        title: t('applyCreator.guide.apply.notificationTitle'),
        content: t('applyCreator.guide.apply.message.failReject', {
          userNcnm: userStore.user?.userNcnm,
        }),
      }),
    // 3. 전환 신청 > 심사중
    underReview: () =>
      dialog({
        title: t('applyCreator.guide.apply.notificationTitle'),
        content: t('applyCreator.guide.apply.message.failUnderReview', {
          userNcnm: userStore.user?.userNcnm,
        }),
      }),
    // 4. KYC 인증 필요
    requireKyc: (title?: string, content?: string, confirmLabel?: string) => {
      dialog({
        title: title || t('applyCreator.requireKycDialog.title'),
        content: content || t('applyCreator.requireKycDialog.message'),
        confirmLabel:
          confirmLabel || t('applyCreator.requireKycDialog.confirm'),
        confirm: () => {
          // 2024.02.21 릴리즈에서 무조건 보상 문구로 변경됨
          // certifiedKYC('service')
          certifiedKYC('reward')
        },
      })
    },
    // 5. 크리에이터 전환 성공
    applySuccess: () => {
      dialog({
        title: t('applyCreator.guide.apply.notificationTitle'),
        content: t('applyCreator.guide.apply.message.successApply'),
        confirm: async () => {
          await useNavigations({ url: '/apply-creator', type: 'replace' })
        },
      })
    },
  }

  const applyCreatorAction = (
    actionType: typeof mypageStore.applyCreatorStatus,
    availableAction?: () => any
  ): (() => any) => {
    const actions: {
      [key in typeof mypageStore.applyCreatorStatus]: () => any
    } = {
      requireSignin: () => {
        showSignInDialog()
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: '로그인 해주세요',
          eventLabel: t('applyCreator.guide.apply.action.apply'),
          eventSlot: '크리에이터 신청하기',
          eventI18nAddr: useKoreanTranslation(
            'applyCreator.guide.apply.action.apply'
          ),
          eventComponent: 'Button',
        })
      },
      approved: () => {
        callDialog.alreadyCreator()
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: useKoreanTranslation(
            'applyCreator.guide.apply.message.failAlreadyCreator'
          ),
          eventLabel: t('applyCreator.guide.apply.action.apply'),
          eventSlot: '크리에이터 신청하기',
          eventI18nAddr: useKoreanTranslation(
            'applyCreator.guide.apply.action.apply'
          ),
          eventComponent: 'Button',
        })
      },
      reject: () => {
        callDialog.reject()
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: useKoreanTranslation(
            'applyCreator.guide.apply.message.failReject'
          ),
          eventLabel: t('applyCreator.guide.apply.action.apply'),
          eventSlot: '크리에이터 신청하기',
          eventI18nAddr: useKoreanTranslation(
            'applyCreator.guide.apply.action.apply'
          ),
          eventComponent: 'Button',
        })
      },
      underReview: () => {
        callDialog.underReview()
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: useKoreanTranslation(
            'applyCreator.guide.apply.message.failUnderReview'
          ),
          eventLabel: t('applyCreator.guide.apply.action.underReview'),
          eventSlot: '크리에이터 신청하기',
          eventI18nAddr: useKoreanTranslation(
            'applyCreator.guide.apply.action.underReview'
          ),
          eventComponent: 'Button',
        })
      },
      available: () => {
        // kyc 인증 여부 검사
        if (!(userStore.user?.kycAt === 'Y')) {
          callDialog.requireKyc()
          gtEvent('clickEvent', {
            eventCategory: '클릭',
            eventAction: useKoreanTranslation(
              'applyCreator.requireKycDialog.message'
            ),
            eventLabel: t('applyCreator.guide.apply.action.apply'),
            eventSlot: '크리에이터 신청하기 > kyc 인증 요청',
            eventI18nAddr: useKoreanTranslation(
              'applyCreator.guide.apply.action.apply'
            ),
            eventComponent: 'Button',
          })
          return
        }
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: '크리에이터 신청 페이지로 이동',
          eventLabel: t('applyCreator.guide.apply.action.apply'),
          eventSlot: '크리에이터 신청',
          eventI18nAddr: useKoreanTranslation(
            'applyCreator.guide.apply.action.apply'
          ),
          eventComponent: 'Button',
        })
        availableAction?.()
      },
    }

    return actions[actionType]
  }

  return { applyCreatorAction, callDialog }
}

export default useApplyCreatorProcess
